/**
 * Shopify Orders — standalone page (Bulk > Bulk Order > Shopify Orders),
 * sibling to Bulk Order List.
 *
 * Lists clensta.com (Shopify) website orders from the ClenstaShopifyOrders
 * collection and drives their shipping:
 *   Shopify → Unicommerce (pick / pack / invoice) → ShipShopy (courier + AWB)
 *   → AWB saved back on Unicommerce → team dispatches on Unicommerce
 *   → Unicommerce syncs the AWB to Shopify.
 *
 * Per row the team does two things here: "Push to ShipShopy" (once the order
 * is packed + invoiced on Unicommerce) and "Assign Courier" (pick from the
 * ShipShopy rate list). The AWB is then written to Unicommerce automatically
 * and the row asks them to dispatch on Unicommerce. Everything else (new
 * orders, Unicommerce status, late AWBs, courier tracking) is kept up to date
 * by crons — see manijment-api services/clenstaShopifyOrderService.js and
 * ai-service/shopifyOrderOps.js. Orders from before the ShipShopy switch
 * (1–12 Oct 2026) are BlueDart shipments, listed for status only.
 *
 * Column headers carry Excel-style filters (funnel icon → tick the values to
 * keep). Filtering, sorting and paging are all server-side; the tick lists
 * come from the API's `facets`. Not to be confused with WebsiteOrderList.js,
 * which is the buyume-web storefront.
 */
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import moment from "moment";
import {
  Grid,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  Button,
  Box,
  Chip,
  Radio,
  Checkbox,
  Popover,
  Tabs,
  Tab,
  Tooltip,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@material-ui/core";
import FilterListIcon from "@mui/icons-material/FilterList";
import { LoadingButton } from "@material-ui/lab";
import MainCard from "../../ui-component/cards/MainCard";
import { canAccessShopifyOrders, canEditShopifyAddresses } from "./shopifyOrderAccess";
import ShopifyAddressDialog from "./ShopifyAddressDialog";

const API = `${process.env.REACT_APP_AI_PATH}/api/ai/shopify-orders`;

export const STATUS_META = {
  CREATED:           { label: "New",                 color: "#9e9e9e" },
  UC_PACKED:         { label: "Ready to Ship",       color: "#0288d1" },
  SHIPSHOPY_CREATED: { label: "On ShipShopy",        color: "#ed6c02" },
  AWB_ASSIGNED:      { label: "AWB Assigned",        color: "#ed6c02" },
  AWB_PUSHED_TO_UC:  { label: "Ready to Dispatch",   color: "#7b1fa2" },
  DISPATCHED:        { label: "Dispatched",          color: "#1565c0" },
  SHOPIFY_SYNCED:    { label: "Dispatched",          color: "#1565c0" },
  IN_TRANSIT:        { label: "In Transit",          color: "#1565c0" },
  OUT_FOR_DELIVERY:  { label: "Out for Delivery",    color: "#1565c0" },
  UNDELIVERED:       { label: "Undelivered",         color: "#d32f2f" },
  DELIVERED:         { label: "Delivered",           color: "#2e7d32" },
  RTO:               { label: "RTO",                 color: "#d32f2f" },
  CANCELLED:         { label: "Cancelled",           color: "#9e9e9e" },
  FAILED:            { label: "Failed",              color: "#d32f2f" },
};

const UC_STAGE_LABEL = {
  CREATED: "Created", PICKING: "Picking", PICKED: "Picked", PACKED: "Packed",
  READY_TO_SHIP: "Ready to Ship", MANIFESTED: "Manifested",
  DISPATCHED: "Dispatched", SHIPPED: "Shipped", DELIVERED: "Delivered",
  RETURN_EXPECTED: "Return Expected", RETURNED: "Returned", CANCELLED: "Cancelled",
  "(none)": "(Blank)",
};

// Unicommerce stage in plain words: Created → Picking → Packed/Invoiced → Ready to Ship → Dispatched.
export function ucStageLabel(uc, invoice) {
  if (!uc?.saleOrderStatus) return "Not on Unicommerce yet";
  if (uc.saleOrderStatus === "CANCELLED") return "Cancelled";
  if (!uc.packageStatus) return "Created";
  if (uc.packageStatus === "PACKED" && invoice?.code) return "Packed & Invoiced";
  return UC_STAGE_LABEL[uc.packageStatus] || uc.packageStatus;
}

// How each filterable column labels its raw values in the tick list.
const FACET_LABEL = {
  status:  (v) => STATUS_META[v]?.label || v,
  payment: (v) => v,
  ucStage: (v) => UC_STAGE_LABEL[v] || v,
  courier: (v) => (v === "(none)" ? "(Blank)" : v),
};

const EMPTY_FILTERS = { status: [], payment: [], ucStage: [], courier: [] };

// Tabs — every order status belongs to exactly one of these (plus "All"), see
// TAB_STATUSES in manijment-api ai-service/shopifyOrderOps.js. The ship /
// assign-courier buttons only ever appear on orders that are eligible, which
// by definition live in "New Orders" (and so also in "All Orders").
const TABS = [
  { key: "all",       label: "All Orders" },
  { key: "new",       label: "New Orders" },
  { key: "inTransit", label: "In-Transit" },
  { key: "delivered", label: "Delivered" },
  { key: "rto",       label: "RTO / Undelivered" },
  { key: "cancelled", label: "Cancelled" },
];

// Summary boxes above the table. Clicking one lists exactly those orders
// (`quick` filter on the API); clicking it again removes the filter.
const BOXES = [
  { key: "pendingShip",     label: "Pending to ship",    hint: "Packed & invoiced on Unicommerce — push to ShipShopy", color: "#d32f2f" },
  { key: "pendingCourier",  label: "Courier not assigned", hint: "On ShipShopy — assign a courier",                   color: "#ed6c02" },
  { key: "pendingDispatch", label: "Pending dispatch",   hint: "AWB is on Unicommerce — dispatch there",              color: "#7b1fa2" },
  { key: "pendingInvoice",  label: "Pending invoice",    hint: "Created on Unicommerce, invoice not made yet",        color: "#0288d1" },
  { key: "dispatched",      label: "Total dispatched",   hint: "Left the warehouse (in transit, delivered, RTO…)",    color: "#2e7d32" },
];

function SummaryBox({ box, count, active, onClick }) {
  return (
    <Tooltip title={`${box.hint}. Click to ${active ? "remove this filter" : "see these orders"}.`}>
      <Box
        onClick={onClick}
        style={{
          cursor: "pointer", minWidth: 150, padding: "8px 14px", borderRadius: 8,
          border: `1px solid ${box.color}`, borderLeft: `5px solid ${box.color}`,
          background: active ? `${box.color}14` : "#fff",
        }}
      >
        <Typography variant="h3" style={{ color: box.color, lineHeight: 1.2 }}>{count ?? "—"}</Typography>
        <Typography variant="caption" color="textSecondary">{box.label}</Typography>
      </Box>
    </Tooltip>
  );
}

/**
 * Excel-style column filter: funnel icon in the header opens a tick list of
 * every value in that column (with counts). Nothing ticked = no filter.
 */
function ColumnFilter({ title, options, selected, labelOf, onApply }) {
  const [anchor, setAnchor] = useState(null);
  const [draft, setDraft] = useState(selected);
  const [query, setQuery] = useState("");
  const active = selected.length > 0;

  const open = (e) => { setDraft(selected); setQuery(""); setAnchor(e.currentTarget); };
  const close = () => setAnchor(null);
  const toggle = (v) => setDraft((d) => (d.includes(v) ? d.filter((x) => x !== v) : [...d, v]));
  const shown = options.filter((o) => labelOf(o.value).toLowerCase().includes(query.toLowerCase()));
  const allShownTicked = shown.length > 0 && shown.every((o) => draft.includes(o.value));
  const toggleAll = () =>
    setDraft(allShownTicked ? draft.filter((v) => !shown.some((o) => o.value === v)) : [...new Set([...draft, ...shown.map((o) => o.value)])]);

  return (
    <Box display="flex" alignItems="center" style={{ whiteSpace: "nowrap" }}>
      {title}
      <Tooltip title={active ? `Filtered: ${selected.map(labelOf).join(", ")}` : "Filter"}>
        <IconButton size="small" onClick={open} style={{ marginLeft: 2, color: active ? "#1565c0" : "#9e9e9e", background: active ? "#e3f2fd" : "transparent" }}>
          <FilterListIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Popover open={!!anchor} anchorEl={anchor} onClose={close} anchorOrigin={{ vertical: "bottom", horizontal: "left" }}>
        <Box p={1.5} style={{ width: 260 }}>
          <TextField size="small" fullWidth placeholder="Search…" value={query} onChange={(e) => setQuery(e.target.value)} />
          <Box style={{ maxHeight: 260, overflowY: "auto", marginTop: 6 }}>
            <Box display="flex" alignItems="center" style={{ cursor: "pointer" }} onClick={toggleAll}>
              <Checkbox size="small" checked={allShownTicked} style={{ padding: 4 }} />
              <Typography variant="body2"><b>(Select all)</b></Typography>
            </Box>
            {shown.map((o) => (
              <Box key={o.value} display="flex" alignItems="center" style={{ cursor: "pointer" }} onClick={() => toggle(o.value)}>
                <Checkbox size="small" checked={draft.includes(o.value)} style={{ padding: 4 }} />
                <Typography variant="body2" style={{ flex: 1 }}>{labelOf(o.value)}</Typography>
                <Typography variant="caption" color="textSecondary">{o.count}</Typography>
              </Box>
            ))}
            {shown.length === 0 && <Typography variant="caption" color="textSecondary">No values</Typography>}
          </Box>
          <Box display="flex" justifyContent="space-between" mt={1}>
            <Button size="small" onClick={() => { onApply([]); close(); }}>Clear filter</Button>
            <Button size="small" variant="contained" onClick={() => { onApply(draft); close(); }}>OK</Button>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
}

// Order column: date range + sort (newest/oldest) instead of a tick list.
function OrderDateFilter({ from, to, sortBy, sortDir, onApply }) {
  const [anchor, setAnchor] = useState(null);
  const [draft, setDraft] = useState({ from, to });
  const active = !!(from || to);
  const close = () => setAnchor(null);
  const sortButton = (dir, label) => (
    <Button
      size="small" fullWidth
      style={{ justifyContent: "flex-start", fontWeight: sortBy === "date" && sortDir === dir ? 700 : 400 }}
      onClick={() => { onApply({ from, to, sortBy: "date", sortDir: dir }); close(); }}
    >
      {label}
    </Button>
  );
  return (
    <Box display="flex" alignItems="center" style={{ whiteSpace: "nowrap" }}>
      Order
      <Tooltip title={active ? `Order date ${from || "…"} → ${to || "…"}` : "Filter by order date / sort"}>
        <IconButton
          size="small" onClick={(e) => { setDraft({ from, to }); setAnchor(e.currentTarget); }}
          style={{ marginLeft: 2, color: active ? "#1565c0" : "#9e9e9e", background: active ? "#e3f2fd" : "transparent" }}
        >
          <FilterListIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Popover open={!!anchor} anchorEl={anchor} onClose={close} anchorOrigin={{ vertical: "bottom", horizontal: "left" }}>
        <Box p={1.5} style={{ width: 240 }}>
          {sortButton("desc", "↓ Newest first")}
          {sortButton("asc", "↑ Oldest first")}
          <Box mt={1}>
            <TextField
              size="small" fullWidth type="date" label="From" InputLabelProps={{ shrink: true }} value={draft.from}
              onChange={(e) => setDraft({ ...draft, from: e.target.value })}
            />
          </Box>
          <Box mt={1}>
            <TextField
              size="small" fullWidth type="date" label="To" InputLabelProps={{ shrink: true }} value={draft.to}
              onChange={(e) => setDraft({ ...draft, to: e.target.value })}
            />
          </Box>
          <Box display="flex" justifyContent="space-between" mt={1}>
            <Button size="small" onClick={() => { onApply({ from: "", to: "", sortBy, sortDir }); close(); }}>Clear dates</Button>
            <Button size="small" variant="contained" onClick={() => { onApply({ ...draft, sortBy, sortDir }); close(); }}>OK</Button>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
}

function CourierDialog({ order, onClose, onAssigned }) {
  const [couriers, setCouriers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [assigning, setAssigning] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/${order._id}/couriers`)
      .then((res) => {
        if (res.data?.success) setCouriers(res.data.couriers || []);
        else setError(res.data?.message || "Could not load couriers");
      })
      .catch((e) => setError(e.response?.data?.message || e.message))
      .finally(() => setLoading(false));
  }, [order._id]);

  const assign = () => {
    setAssigning(true);
    axios
      .post(`${API}/${order._id}/assign-courier`, { courierId: selected.id, courierName: selected.name })
      .then((res) => {
        if (res.data?.message) alert(res.data.message);
        if (res.data?.order) onAssigned(res.data.order);
        if (res.data?.success) onClose();
      })
      .catch((e) => alert(e.response?.data?.message || e.message))
      .finally(() => setAssigning(false));
  };

  return (
    <Dialog open onClose={assigning ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Assign courier — {order.orderName}
        <Typography variant="caption" display="block" color="textSecondary">
          {order.city} {order.pincode} · {order.payment?.type} ₹{order.payment?.totalAmount}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {loading && <Box textAlign="center" p={3}><CircularProgress size={28} /></Box>}
        {!loading && error && <Typography color="error">{error}</Typography>}
        {!loading && !error && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>Courier</TableCell>
                <TableCell align="right">Charge (₹)</TableCell>
                <TableCell>Min. weight</TableCell>
                <TableCell>Est. delivery</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {couriers.map((c) => (
                <TableRow key={c.id} hover selected={selected?.id === c.id} onClick={() => setSelected(c)} style={{ cursor: "pointer" }}>
                  <TableCell padding="checkbox"><Radio size="small" checked={selected?.id === c.id} /></TableCell>
                  <TableCell>{c.name}</TableCell>
                  <TableCell align="right">{c.totalCharges}</TableCell>
                  <TableCell>{c.minimumChargeableWeight || "—"}</TableCell>
                  <TableCell>{c.estimatedDelivery || "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={assigning}>Cancel</Button>
        <LoadingButton variant="contained" loading={assigning} disabled={!selected} onClick={assign}>
          Assign{selected ? ` ${selected.name}` : ""}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

// Access gate (admins + allowlist) — kept outside the page component so the
// page's hooks/API calls never run for anyone else. The API 403s them too.
export default function ShopifyOrderList() {
  if (!canAccessShopifyOrders()) {
    return <MainCard><Typography color="error">You do not have access to Shopify Orders.</Typography></MainCard>;
  }
  return <ShopifyOrderListPage />;
}

function ShopifyOrderListPage() {
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [facets, setFacets] = useState({ status: [], payment: [], ucStage: [], courier: [] });
  const [tabCounts, setTabCounts] = useState({});
  const [summary, setSummary] = useState({});
  // Everything that shapes the list lives in one object so a change to any
  // filter triggers exactly one reload (see the effect below).
  const [view, setView] = useState({
    tab: "all", quick: "",
    page: 0, rowsPerPage: 25, search: "", from: "", to: "", sortBy: "date", sortDir: "desc", filters: EMPTY_FILTERS,
  });
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [courierOrder, setCourierOrder] = useState(null);
  const [addressOrderId, setAddressOrderId] = useState(null);
  const requestSeq = useRef(0);

  const load = (v = view) => {
    const seq = ++requestSeq.current;
    setLoading(true);
    return axios
      .get(`${API}/list`, {
        params: {
          tab: v.tab, quick: v.quick || undefined,
          page: v.page + 1, limit: v.rowsPerPage,
          search: v.search || undefined, from: v.from || undefined, to: v.to || undefined,
          sortBy: v.sortBy, sortDir: v.sortDir,
          status: v.filters.status.join(",") || undefined,
          payment: v.filters.payment.join(",") || undefined,
          ucStage: v.filters.ucStage.join(",") || undefined,
          courier: v.filters.courier.join(",") || undefined,
        },
      })
      .then((res) => {
        if (seq !== requestSeq.current) return; // a newer request superseded this one
        if (res.data?.success) {
          setOrders(res.data.orders || []);
          setTotal(res.data.total || 0);
          setFacets(res.data.facets || {});
          setTabCounts(res.data.tabCounts || {});
          setSummary(res.data.summary || {});
        } else {
          alert(res.data?.message || "Something went wrong");
        }
      })
      .catch((e) => console.log("error loading shopify orders", e))
      .finally(() => { if (seq === requestSeq.current) setLoading(false); });
  };

  useEffect(() => {
    load(view);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const setFilter = (key, values) => setView((v) => ({ ...v, page: 0, filters: { ...v.filters, [key]: values } }));
  const activeFilterCount = Object.values(view.filters).filter((a) => a.length).length + (view.from || view.to ? 1 : 0) + (view.search ? 1 : 0) + (view.quick ? 1 : 0);
  const clearAll = () => { setSearchText(""); setView((v) => ({ ...v, page: 0, quick: "", search: "", from: "", to: "", filters: EMPTY_FILTERS })); };
  // Switching tab keeps search/date but drops the column ticks and box filter —
  // their values belong to the tab they were picked in.
  const changeTab = (tab) => setView((v) => ({ ...v, tab, quick: "", page: 0, filters: EMPTY_FILTERS }));
  // A box is a filter over all orders, so it always shows on the "All Orders" tab.
  const toggleBox = (key) => setView((v) => ({ ...v, tab: "all", quick: v.quick === key ? "" : key, page: 0, filters: EMPTY_FILTERS }));

  const replaceRow = (row) => setOrders((prev) => prev.map((o) => (o._id === row._id ? row : o)));

  const syncNow = () => {
    setSyncing(true);
    axios
      .post(`${API}/sync`)
      .then((res) => { if (!res.data?.success) alert(res.data?.message || "Sync failed"); })
      .catch((e) => alert(e.response?.data?.message || e.message))
      .finally(() => { setSyncing(false); load(); });
  };

  const rowAction = (order, path) => {
    setBusyId(order._id);
    axios
      .post(`${API}/${order._id}/${path}`)
      .then((res) => {
        if (res.data?.order) replaceRow(res.data.order);
        if (!res.data?.success && res.data?.message) alert(res.data.message);
      })
      .catch((e) => alert(e.response?.data?.message || e.message))
      .finally(() => setBusyId(null));
  };

  const downloadInvoice = (order) => {
    setBusyId(order._id);
    axios
      .get(`${API}/${order._id}/invoice`, { responseType: "blob" })
      .then((res) => {
        const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
        const a = document.createElement("a");
        a.href = url;
        a.download = `Invoice-${order.orderName}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => alert("Could not download the invoice from Unicommerce"))
      .finally(() => setBusyId(null));
  };

  const downloadLabel = (order) => {
    setBusyId(order._id);
    axios
      .get(`${API}/${order._id}/label`)
      .then((res) => {
        if (res.data?.success && res.data.url) window.open(res.data.url, "_blank", "noopener,noreferrer");
        else alert(res.data?.message || "Label not available");
      })
      .catch((e) => alert(e.response?.data?.message || e.message))
      .finally(() => setBusyId(null));
  };

  const renderAction = (o) => {
    const busy = busyId === o._id;
    switch (o.nextAction) {
      case "PUSH_TO_SHIPSHOPY":
        return <LoadingButton size="small" variant="contained" loading={busy} onClick={() => rowAction(o, "push-shipshopy")}>Push to ShipShopy</LoadingButton>;
      case "ASSIGN_COURIER":
        return <Button size="small" variant="contained" color="secondary" disabled={busy} onClick={() => setCourierOrder(o)}>Assign Courier</Button>;
      case "AWB_PENDING":
        return <Typography variant="caption" color="textSecondary">Waiting for AWB from ShipShopy…</Typography>;
      case "UC_AWB_PENDING":
        return <LoadingButton size="small" variant="outlined" loading={busy} onClick={() => rowAction(o, "refresh")}>Retry Unicommerce update</LoadingButton>;
      case "DISPATCH_ON_UNICOMMERCE":
        return <Typography variant="caption" color="textSecondary">Dispatch on Unicommerce</Typography>;
      default:
        return null;
    }
  };

  const sortAmount = () => setView((v) => ({ ...v, page: 0, sortBy: "amount", sortDir: v.sortBy === "amount" && v.sortDir === "desc" ? "asc" : "desc" }));
  const applySearch = () => setView((v) => ({ ...v, page: 0, search: searchText.trim() }));

  return (
    <MainCard title="Shopify Orders (clensta.com)">
      <Box display="flex" flexWrap="wrap" style={{ gap: 12, marginBottom: 12 }}>
        {BOXES.map((b) => (
          <SummaryBox key={b.key} box={b} count={summary[b.key]} active={view.quick === b.key} onClick={() => toggleBox(b.key)} />
        ))}
      </Box>

      <Tabs
        value={view.tab} onChange={(e, t) => changeTab(t)} variant="scrollable" scrollButtons="auto"
        indicatorColor="primary" textColor="primary" style={{ marginBottom: 12, borderBottom: "1px solid #e0e0e0" }}
      >
        {TABS.map((t) => (
          <Tab key={t.key} value={t.key} label={`${t.label} (${tabCounts[t.key] ?? 0})`} />
        ))}
      </Tabs>

      <Grid container spacing={2} alignItems="center" style={{ marginBottom: 12 }}>
        <Grid item>
          <TextField
            size="small" label="Order ID / phone / AWB" value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") applySearch(); }}
          />
        </Grid>
        <Grid item>
          <Button variant="outlined" onClick={applySearch}>Search</Button>
        </Grid>
        <Grid item>
          <Typography variant="body2" color="textSecondary">
            {view.quick ? `${BOXES.find((b) => b.key === view.quick)?.label}: ` : ""}{total} order{total === 1 ? "" : "s"}{activeFilterCount ? ` · ${activeFilterCount} filter${activeFilterCount === 1 ? "" : "s"} on` : ""}
          </Typography>
        </Grid>
        {activeFilterCount > 0 && (
          <Grid item>
            <Button size="small" onClick={clearAll}>Clear all filters</Button>
          </Grid>
        )}
        <Grid item xs />
        <Grid item>
          <Tooltip title="Pull new orders from Shopify and refresh Unicommerce status now (also runs automatically every 10 minutes)">
            <span>
              <LoadingButton variant="contained" loading={syncing} onClick={syncNow}>Sync now</LoadingButton>
            </span>
          </Tooltip>
        </Grid>
      </Grid>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <OrderDateFilter
                  from={view.from} to={view.to} sortBy={view.sortBy} sortDir={view.sortDir}
                  onApply={(d) => setView((v) => ({ ...v, page: 0, ...d }))}
                />
              </TableCell>
              <TableCell>Customer</TableCell>
              <TableCell align="right">
                <Tooltip title="Sort by amount">
                  <span style={{ cursor: "pointer", whiteSpace: "nowrap" }} onClick={sortAmount}>
                    Amount{view.sortBy === "amount" ? (view.sortDir === "desc" ? " ↓" : " ↑") : " ⇅"}
                  </span>
                </Tooltip>
              </TableCell>
              <TableCell>
                <ColumnFilter title="Payment" options={facets.payment || []} selected={view.filters.payment} labelOf={FACET_LABEL.payment} onApply={(v) => setFilter("payment", v)} />
              </TableCell>
              <TableCell>
                <ColumnFilter title="Unicommerce" options={facets.ucStage || []} selected={view.filters.ucStage} labelOf={FACET_LABEL.ucStage} onApply={(v) => setFilter("ucStage", v)} />
              </TableCell>
              <TableCell>
                <ColumnFilter title="Courier / AWB" options={facets.courier || []} selected={view.filters.courier} labelOf={FACET_LABEL.courier} onApply={(v) => setFilter("courier", v)} />
              </TableCell>
              <TableCell>
                <ColumnFilter title="Status" options={facets.status || []} selected={view.filters.status} labelOf={FACET_LABEL.status} onApply={(v) => setFilter("status", v)} />
              </TableCell>
              <TableCell>Downloads</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={9} align="center"><CircularProgress size={24} /></TableCell></TableRow>
            )}
            {!loading && orders.length === 0 && (
              <TableRow><TableCell colSpan={9} align="center">No orders found</TableCell></TableRow>
            )}
            {!loading && orders.map((o) => {
              const meta = STATUS_META[o.currentStatus] || { label: o.currentStatus, color: "#9e9e9e" };
              const busy = busyId === o._id;
              const isCod = o.payment?.type === "COD";
              return (
                <TableRow key={o._id} hover>
                  <TableCell>
                    <Typography variant="subtitle2">{o.orderName}</Typography>
                    <Typography variant="caption" color="textSecondary" display="block">
                      {moment(o.shopifyCreatedAt).format("DD/MM/YYYY hh:mm A")}
                    </Typography>
                    {o.shopifyCancelledAt && o.currentStatus !== "CANCELLED" && (
                      <Chip size="small" variant="outlined" label="Cancelled on Shopify" style={{ borderColor: "#d32f2f", color: "#d32f2f" }} />
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{o.customer?.name || "—"}</Typography>
                    <Typography variant="caption" color="textSecondary" display="block">{o.customer?.phone}</Typography>
                    <Typography variant="caption" color="textSecondary" display="block">{o.city} {o.pincode}</Typography>
                  </TableCell>
                  <TableCell align="right">₹{o.payment?.totalAmount}</TableCell>
                  <TableCell>
                    <Chip size="small" variant="outlined" label={o.payment?.type || "—"} style={{ borderColor: isCod ? "#ed6c02" : "#2e7d32", color: isCod ? "#ed6c02" : "#2e7d32" }} />
                    {isCod && (
                      <Typography variant="caption" color="textSecondary" display="block">Collect ₹{o.payment.codAmount}</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{ucStageLabel(o.unicommerce, o.invoice)}</Typography>
                    {o.invoice?.code && <Typography variant="caption" color="textSecondary" display="block">Inv: {o.invoice.code}</Typography>}
                  </TableCell>
                  <TableCell>
                    {o.shipshopy?.orderId && (
                      <>
                        <Typography variant="body2">{o.shipshopy.courierName || "ShipShopy — no courier yet"}</Typography>
                        {o.shipshopy.awbNumber && (
                          <Typography variant="caption" display="block">
                            AWB: <a href={o.shipshopy.trackingUrl} target="_blank" rel="noopener noreferrer">{o.shipshopy.awbNumber}</a>
                          </Typography>
                        )}
                        <Typography variant="caption" color="textSecondary" display="block">
                          ShipShopy {o.shipshopy.orderId}{o.shipshopy.shippingCharge ? ` · ₹${o.shipshopy.shippingCharge}` : ""}
                        </Typography>
                      </>
                    )}
                    {!o.shipshopy?.orderId && o.unicommerce?.trackingNumber && (
                      <>
                        <Typography variant="body2">{o.unicommerce.shippingProvider}</Typography>
                        <Typography variant="caption" display="block">
                          AWB:{" "}
                          {o.shopifySync?.trackingUrl
                            ? <a href={o.shopifySync.trackingUrl} target="_blank" rel="noopener noreferrer">{o.unicommerce.trackingNumber}</a>
                            : o.unicommerce.trackingNumber}
                        </Typography>
                      </>
                    )}
                    {!o.shipshopy?.orderId && !o.unicommerce?.trackingNumber && (
                      <Typography variant="caption" color="textSecondary">Not shipped</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={meta.label} style={{ backgroundColor: meta.color, color: "#fff" }} />
                    {o.tracking?.courierStatus && (
                      <Tooltip title={o.tracking.statusTime ? moment(o.tracking.statusTime).format("DD/MM/YYYY hh:mm A") : ""}>
                        <Typography variant="caption" color="textSecondary" display="block" style={{ maxWidth: 190 }}>{o.tracking.courierStatus}</Typography>
                      </Tooltip>
                    )}
                    {o.unicommerce?.awbPushed && (
                      <Typography variant="caption" display="block" style={{ color: o.shopifySync?.synced ? "#2e7d32" : "#9e9e9e" }}>
                        {o.shopifySync?.synced ? "✓ AWB on Shopify" : "AWB not on Shopify yet"}
                      </Typography>
                    )}
                    {o.lastError && (
                      <Tooltip title={`${o.lastError.step}: ${o.lastError.message}`}>
                        <Typography variant="caption" color="error" display="block" noWrap style={{ maxWidth: 190 }}>
                          ⚠ {o.lastError.message}
                        </Typography>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>
                    <Button size="small" disabled={!o.invoice?.code || busy} onClick={() => downloadInvoice(o)}>Invoice</Button>
                    <Button size="small" disabled={!o.shipshopy?.awbNumber || busy} onClick={() => downloadLabel(o)}>Label</Button>
                  </TableCell>
                  <TableCell>
                    {renderAction(o)}
                    {o.canEditAddress && canEditShopifyAddresses() && (
                      <Box mt={0.5}>
                        <Button size="small" disabled={busy} onClick={() => setAddressOrderId(o._id)} style={{ fontSize: 11 }}>
                          ✎ Edit address
                        </Button>
                      </Box>
                    )}
                    <Box mt={0.5} style={{ whiteSpace: "nowrap" }}>
                      <Button
                        size="small" variant="outlined" style={{ marginRight: 4 }}
                        onClick={() => window.open(`/crm/bulk/shopify-order-details/${o._id}`, "_blank", "noopener,noreferrer")}
                      >
                        Details
                      </Button>
                      <Button size="small" disabled={busy} onClick={() => rowAction(o, "refresh")} style={{ minWidth: 0, padding: "0 4px", fontSize: 11 }}>
                        ↻ refresh
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={total}
        page={view.page}
        rowsPerPage={view.rowsPerPage}
        rowsPerPageOptions={[25, 50, 100, 200]}
        onPageChange={(e, p) => setView((v) => ({ ...v, page: p }))}
        onRowsPerPageChange={(e) => setView((v) => ({ ...v, page: 0, rowsPerPage: parseInt(e.target.value, 10) }))}
      />

      {addressOrderId && (
        <ShopifyAddressDialog orderId={addressOrderId} onClose={() => setAddressOrderId(null)} onSaved={replaceRow} />
      )}

      {courierOrder && (
        <CourierDialog
          order={courierOrder}
          onClose={() => setCourierOrder(null)}
          onAssigned={replaceRow}
        />
      )}
    </MainCard>
  );
}
